import { fetchClient } from "@/api/api";
import type {
	Urls,
	loginSchemaOutput,
	refreshTokenSchemaOutput,
} from "@/api/custom_types";
import type { ApiErrorType, ErrorType } from "@/api/utils";
import { baseUrlAtom } from "@/store/base_api";
import { currentUserAtom } from "@/store/login";
import { routerAtom } from "@/store/router";
import {
	TELEGRAM_CONNECT_PATH,
	rememberTelegramConnectTarget,
} from "@/store/telegram";
import { MutationObserver } from "@tanstack/react-query";
import { getDefaultStore } from "jotai";
import { getCookie } from "typescript-cookie";
import { queryClient, waitForMutation } from "./react-query";

export class ApiError extends Error {
	status: number;
	url: string;

	constructor(
		message: string,
		public response: Response,
		public body: ApiErrorType | string,
	) {
		super(message);
		this.response = response;
		this.status = response.status;
		this.url = response.url;
	}

	static async fromRequestResponse(
		message: string,
		request: Request,
		response: Response,
	) {
		const url = new URL(request.url);
		const msg = `${request.method} ${url.pathname} ${response.status} : ${message}`;
		const body = await response
			.json()
			.catch(() => response.text().catch(() => null));
		const error = new ApiError(msg, response, body);
		return error;
	}
}

function mergeHeaders(
	headers: HeadersInit,
	newHeaders: HeadersInit,
): HeadersInit {
	const mergedHeaders = new Headers(headers);
	for (const [key, value] of Object.entries(newHeaders)) {
		mergedHeaders.set(key, value);
	}
	return mergedHeaders;
}

function mergeRequestInit(base: RequestInit, apply: RequestInit): RequestInit {
	for (const [key, value] of Object.entries(apply)) {
		if (key === "headers") {
			continue;
		}
		base[key as keyof RequestInit] = value;
	}

	if (apply.headers) {
		if (base.headers) base.headers = mergeHeaders(base.headers, apply.headers);
		else base.headers = apply.headers;
	}

	return base;
}

function isUnauthorized(response: Response): boolean {
	const store = getDefaultStore();
	const base = store.get(baseUrlAtom);

	if (response.status !== 401) return false;

	const ignorePaths = new Set<Urls>([
		"/v1/fc/logout",
		"/v1/fc/refresh",
		"/v1/fc/login",
	]);

	const resUrl = new URL(response.url);
	const baseUrl = new URL(base);

	if (
		resUrl.origin === baseUrl.origin &&
		ignorePaths.has(resUrl.pathname as Urls)
	) {
		return false;
	}

	return true;
}

async function handleUnauthorized(request: Request, response: Response) {
	const store = getDefaultStore();
	const accessErrorTypes = [
		"AccessTokenMissing",
		"ExpiredAccessToken",
		"InvalidAccessToken",
	] as const satisfies ErrorType[];

	const body = await response.json();
	try {
		if (!accessErrorTypes.includes(body.type)) {
			throw await ApiError.fromRequestResponse(
				"Can't refresh",
				request,
				response,
			);
		}
		await safeRefresh();
		await queryClient.cancelQueries();
		await queryClient.invalidateQueries();
		return;
	} catch {
		store.set(currentUserAtom, null);
		redirectToLogin();
	}
	throw await ApiError.fromRequestResponse("Unauthorized", request, response);
}

/**
 * Sends the user to /login without losing where they were. Bootstrap queries
 * 401 on every page, so this can fire several times in a row: re-navigating to
 * /login while already there resets the login form mid-typing.
 */
export function redirectToLogin() {
	const router = getDefaultStore().get(routerAtom);
	const { pathname, searchStr } = router.state.location;

	if (pathname === TELEGRAM_CONNECT_PATH) {
		rememberTelegramConnectTarget(new URLSearchParams(searchStr).get("token"));
	}

	if (pathname === "/login") {
		return;
	}

	router.navigate({ replace: true, to: "/login" });
}

const AUTH_HEADERS = {
	"X-Csrf-Token": "X-Csrf-Token",
};

export function buildRequest(url: string, init: RequestInit): Request {
	const headers: HeadersInit = {};
	for (const [key, cookie_name] of Object.entries(AUTH_HEADERS)) {
		const value = getCookie(cookie_name);
		if (value) {
			headers[key] = value;
		}
	}
	const default_init: RequestInit = {
		credentials: "include",
		headers: headers,
	};
	return new Request(url, mergeRequestInit(default_init, init));
}

export async function fetchRequest(request: Request) {
	const response = await fetch(request);
	if (isUnauthorized(response)) {
		await handleUnauthorized(request, response);
	} else if (!response.ok) {
		throw await ApiError.fromRequestResponse("API Error", request, response);
	}
	return response;
}

export function safeRefresh() {
	// If there is already a refresh in progress, wait for it to finish
	const inFlight = queryClient.isMutating({
		mutationKey: ["refresh"],
	});
	const refreshObserver = new MutationObserver<refreshTokenSchemaOutput, Error>(
		queryClient,
		{
			mutationKey: ["refresh"],
			mutationFn: async () => {
				const response = await fetchClient.POST("/v1/fc/refresh", {
					throwOnError: true,
				});
				if (response.error) {
					throw await ApiError.fromRequestResponse(
						"Refresh token failed",
						new Request("/v1/fc/refresh", { method: "POST" }),
						response.response,
					);
				}
				return response.data;
			},
			onSuccess: (data) => {
				const payload = {
					role: data.user_role,
					username: data.username,
					id: data.id,
					need_follow_otp: false,
				} satisfies loginSchemaOutput;
				getDefaultStore().set(currentUserAtom, payload);
			},
		},
	);
	if (inFlight > 0) {
		return waitForMutation(refreshObserver);
	}
	// otherwise, start a new refresh
	return refreshObserver.mutate();
}
